"use client";

import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

import { useCurrency } from "@/features/currency/_hooks/useCurrency";

import { useEventProposals } from "../_hooks/useEventProposals";
import { useFlightSearch, type FlightSearchState } from "../_hooks/useFlightSearch";
import { useFxRates } from "../_hooks/useFxRates";
import { useHotelRates } from "../_hooks/useHotelRates";
import { COMPACT_AMOUNT_FROM } from "../_logic/constants";
import { buildEstimate, fromUsd, toUsd } from "../_logic/estimate";
import { proposalRatesByCity, type ProposalRates } from "../_logic/proposal-rates";
import type { FaresSnapshot } from "../_logic/saved/ui-types";
import type { EditSource, Estimate, EstimatorMoney, HotelRate, Workspace, WorkspaceAction } from "../_logic/types";
import { applyAction, createWorkspace, inverseOf, rejectReason } from "../_logic/workspace";

/** Result of one edit: the inverse to undo it, or why it could not apply. */
export type EditResult = { ok: true; undo: WorkspaceAction } | { ok: false; error: string };

interface EstimatorContextValue {
  /** False on the start screen, true once there is an estimate to show. */
  started: boolean;
  workspace: Workspace;
  estimate: Estimate;
  /** The flight pipeline's search for the workspace. */
  flights: FlightSearchState;
  hotels: ReadonlyMap<string, HotelRate>;
  /** Reads every destination's hotel rates again (Refresh fares, or Retry after a failed lookup). */
  refreshHotels: () => void;
  rates: Readonly<Record<string, number>>;
  money: EstimatorMoney;
  /** Applies one edit and returns its inverse. Reads the latest workspace, so back-to-back edits compose. */
  edit: (action: WorkspaceAction, source: EditSource) => EditResult;
  /** Leaves the start screen, optionally with a prefilled workspace. */
  start: (workspace?: Workspace) => void;
  /** The event this estimate is for (linked, or opened from); the assistant and proposals read it. */
  eventId: number | null;
  setEventId: (eventId: number | null) => void;
  /**
   * Whether costs read per person or for the whole group. One setting for the list, the open
   * breakdowns, the side-by-side table, the header and the assistant.
   */
  costView: CostView;
  setCostView: (view: CostView) => void;
  /** Latest workspace and estimate, for async readers such as the chat's tool handlers. */
  latest: React.MutableRefObject<LatestEstimate>;
}

/** Snapshot of the estimate for readers outside the render cycle. */
export interface LatestEstimate {
  workspace: Workspace;
  estimate: Estimate;
  rates: Readonly<Record<string, number>>;
  /** The page's money formatting, so the assistant's steps read like the table. */
  money: EstimatorMoney;
  fares: FlightSearchState["quotes"];
  hotels: ReadonlyMap<string, HotelRate>;
  /** The estimate's event, for the assistant. */
  eventId: number | null;
  /** Per person or group totals, as the planner is viewing them. */
  costView: CostView;
}

/** How costs read: per traveler, or for the whole group. */
export type CostView = "perPerson" | "total";

/**
 * Joins two formatted amounts as a range, each with its currency symbol so neither end reads
 * as a bare number: "$4.7K–$7.3K", "£280–£420".
 */
const joinRange = (low: string, high: string): string => `${low}–${high}`;

const EstimatorContext = createContext<EstimatorContextValue | null>(null);

/**
 * Holds the estimate workspace and everything derived from it: fares, hotel rates, the
 * computed comparison and currency helpers. Every editor (table, chips, chat) goes through `edit`.
 * @param props.children - The estimator page.
 * @param props.saved - A saved (or pre-filled) estimate to open: its workspace and the fares it was priced with.
 * @param props.initialEventId - The event it is linked to or opened from.
 * @returns The provider.
 * @throws Never.
 */
export const EstimatorProvider = ({
  children,
  saved,
  initialEventId = null,
}: {
  children: ReactNode;
  saved?: { workspace: Workspace; fares: FaresSnapshot | null; faresCheckedAt: string | null };
  initialEventId?: number | null;
}) => {
  // Hooks
  const { targetCurrency, formatNative } = useCurrency();

  // States
  const [started, setStarted] = useState(Boolean(saved));
  const [workspace, setWorkspace] = useState<Workspace>(() => saved?.workspace ?? createWorkspace(targetCurrency));
  const workspaceRef = useRef(workspace);
  const [eventId, setEventId] = useState<number | null>(initialEventId);
  const [costView, setCostView] = useState<CostView>("perPerson");
  const [savedFares] = useState(() => (saved ? { fares: saved.fares, checkedAt: saved.faresCheckedAt } : undefined));

  const flights = useFlightSearch(workspace, savedFares);
  const { rates: hotels, refresh: refreshHotels } = useHotelRates(workspace.destinations, workspace.airports);
  const rates = useFxRates(workspace.currency);
  const quotes = useEventProposals(eventId);

  // Derived
  const proposals = useMemo(() => proposalRatesByCity(quotes), [quotes]);
  const proposalsRef = useRef<ReadonlyMap<string, ProposalRates>>(proposals);
  proposalsRef.current = proposals;
  const estimate = useMemo(
    () => buildEstimate(workspace, flights.quotes, hotels, proposals),
    [workspace, flights.quotes, hotels, proposals]
  );
  const money = useMemo<EstimatorMoney>(() => {
    const currency = workspace.currency;
    const convert = (usd: number) => fromUsd(usd, currency, rates);
    const display = (usd: number) => convert(usd) ?? usd;
    const code = convert(1) === null ? "USD" : currency;
    // Compact keeps one decimal ("$4.7K"), so a group total doesn't round to the nearest thousand.
    // Large amounts are always compact ("¥1.9M"), so a yen total fits its column.
    const format = (usd: number, compact = false) =>
      formatNative(
        display(usd),
        code,
        compact || Math.abs(display(usd)) >= COMPACT_AMOUNT_FROM ? { compact: true } : { noDecimal: true }
      );
    const usdPerUnit = rates[currency];
    return {
      format,
      formatDisplay: (amount) => formatNative(amount, code, { noDecimal: true }),
      // Ends that read the same once formatted ("¥2.2M") show once.
      formatRange: (range, compact = false) => {
        const low = format(range.low, compact);
        const high = format(range.high, compact);
        return low === high ? low : joinRange(low, high);
      },
      toUsd: (amount) => toUsd(amount, currency, rates),
      fromUsd: convert,
      fxNote: currency !== "USD" && usdPerUnit ? `1 USD = ${(1 / usdPerUnit).toFixed(2)} ${currency}` : null,
    };
  }, [workspace.currency, rates, formatNative]);
  const latest = useRef<LatestEstimate>({
    workspace,
    estimate,
    rates,
    money,
    fares: flights.quotes,
    hotels,
    eventId,
    costView,
  });
  latest.current = { workspace, estimate, rates, money, fares: flights.quotes, hotels, eventId, costView };

  // Handlers
  const edit = useCallback((action: WorkspaceAction, source: EditSource): EditResult => {
    const before = workspaceRef.current;
    const reason = rejectReason(before, action);
    if (reason) return { ok: false, error: reason };
    const next = applyAction(before, action, source);
    workspaceRef.current = next;
    // Readers outside React (the chat's next request, read_estimate) see the edit before the
    // re-render. Fares and hotels for anything new read as loading until their effects run.
    const known = latest.current;
    latest.current = {
      ...known,
      workspace: next,
      estimate: buildEstimate(next, known.fares, known.hotels, proposalsRef.current),
    };
    setWorkspace(next);
    setStarted(true);
    return { ok: true, undo: inverseOf(before, action) };
  }, []);

  const start = useCallback((prefill?: Workspace) => {
    if (prefill) {
      workspaceRef.current = prefill;
      setWorkspace(prefill);
    }
    setStarted(true);
  }, []);

  // Render
  const value = useMemo<EstimatorContextValue>(
    () => ({
      started,
      workspace,
      estimate,
      flights,
      hotels,
      refreshHotels,
      rates,
      money,
      edit,
      start,
      eventId,
      setEventId,
      costView,
      setCostView,
      latest,
    }),
    [started, workspace, estimate, flights, hotels, refreshHotels, rates, money, edit, start, eventId, costView]
  );
  return <EstimatorContext.Provider value={value}>{children}</EstimatorContext.Provider>;
};

/**
 * The estimator's workspace context.
 * @returns The context value.
 * @throws When used outside {@link EstimatorProvider}.
 */
export function useEstimator(): EstimatorContextValue {
  const value = useContext(EstimatorContext);
  if (!value) throw new Error("useEstimator must be used inside EstimatorProvider");
  return value;
}
